/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { createLogPrefix, createPersistentItem, logError, type LogTarget } from '@amazon/vinyl-util'
import { boolean } from '@amazon/vinyl-validation'
import { inviteGateDisabled, verifyInvite } from '@/auth/inviteToken'
import { getDeviceConfig } from '@/device/deviceConfig'

declare global {
    // Set true by the static render worker (PRERENDER_INVITE_GLOBAL) so a render
    // treats the invite as already accepted; never set on served pages.
    var __prerenderInvite: boolean | undefined
}

const target: LogTarget = { logPrefix: createLogPrefix('inviteGate') }

// Device-level unlock flag: once a valid invite unlocks this browser, it stays
// unlocked permanently. A validated persistent item rejects a corrupt / legacy
// stored value and falls back to `false`.
const inviteAcceptedItem = createPersistentItem<boolean>({
    key: 'ampInviteAccepted',
    defaultValue: false,
    validator: boolean().assert,
})

/**
 * App-load access gate for the hosted build. Runs once before the route mounts:
 * true means the app may boot, false means the invite wall shows instead.
 *
 * Order: a device config without `requiresInvite` (a native shell, a trusted
 * distribution channel) is allowed before any invite is considered; then gate-disabled (dev) and the sticky
 * unlock flag short-circuit straight to allowed; otherwise the `?invite=` token
 * is verified and, when valid, the browser is stickily unlocked. The stickiness
 * is what keeps an iOS home-screen PWA — which launches in its own localStorage
 * partition — unlocked after the non-expiring token in `start_url` re-validates
 * on its first launch.
 */
export async function checkInviteGate(): Promise<boolean> {
    if (!getDeviceConfig().requiresInvite) return true
    if (inviteGateDisabled()) return true
    if (globalThis.__prerenderInvite === true) return true
    if (await inviteAccepted()) return true

    const token = new URLSearchParams(location.search).get('invite')
    if ((await verifyInvite(token)) !== 'valid') return false

    markInviteAccepted()
    return true
}

async function inviteAccepted(): Promise<boolean> {
    try {
        return await inviteAcceptedItem.get()
    } catch (e) {
        logError(target, e)
        return false
    }
}

function markInviteAccepted(): void {
    inviteAcceptedItem.set(true).catch((e) => logError(target, e))
}
